/**
 * How a person's details are SHOWN, never how they are stored.
 *
 * Stores send names and addresses in whatever case the shopper typed or the
 * checkout app forced: "SOUTH WEST, DL", "vipul dave", "SOMADITYA RATHI".
 * Printed raw, a record shouts in one place and whispers in the next, and
 * it reads as a data dump rather than a page someone looked after. These
 * helpers only touch text that is ALL upper or ALL lower case; anything with
 * deliberate mixed case (McDonald, iPhone, DeSouza) is left exactly as typed.
 */
const TWO_DIGIT_CC = new Set(['20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98']);
const SMALL_WORDS = new Set(['and', 'of', 'the', 'de', 'da', 'van', 'von', 'bin', 'al']);

const MIN_MS = 60 * 1000;
const HOUR_MS = 60 * MIN_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How long ago, in words: "just now", "4 min ago", "1 hour ago", "3 days ago",
 * "2 months ago", "1 year ago". THE helper for every "last seen / last synced /
 * last event" line; there were a dozen, each with its own units ("5m", "5 min",
 * "5 h") and its own cut-offs, so one card said "47h ago" beside another's
 * "2 days ago" for the same moment. No time returns `never` (null unless given).
 */
export function timeAgo(ts, { now = Date.now(), never = null } = {}) {
  if (ts == null || ts === '') return never;
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return never;
  const d = Math.max(0, now - t);
  if (d < MIN_MS) return 'just now';
  if (d < HOUR_MS) return `${Math.floor(d / MIN_MS)} min ago`;
  if (d < DAY_MS) { const h = Math.floor(d / HOUR_MS); return `${h} ${h === 1 ? 'hour' : 'hours'} ago`; }
  const n = Math.floor(d / DAY_MS);
  if (n < 60) return `${n} ${n === 1 ? 'day' : 'days'} ago`;
  if (n < 365) return `${Math.floor(n / 30)} months ago`;
  const y = Math.floor(n / 365);
  return `${y} ${y === 1 ? 'year' : 'years'} ago`;
}

/**
 * A reporting window in words, the ONE place "N days" is spelt. A window of
 * one day is Today in the picker, so it must read "today" here too; a label
 * built as `${win} days` printed "Sent, 1 days" the moment Today was picked.
 *
 *   dayWindow(28)            -> "28 days"             (a column or tile suffix)
 *   dayWindow(1)             -> "today"
 *   dayWindow(28, 'in')      -> "in the last 28 days" (inside a sentence)
 *   dayWindow(1, 'in')       -> "today"
 *   dayWindow(28, 'caption') -> "Last 28 days"        (a caption on its own)
 *   dayWindow(1, 'caption')  -> "Today"
 *   dayWindow('all', ...)    -> "all time" / "All time"
 */
export function dayWindow(days, form = 'span') {
  if (days === 'all') return form === 'caption' ? 'All time' : 'all time';
  const n = Number(days);
  if (n === 1) return form === 'caption' ? 'Today' : 'today';
  if (form === 'in') return `in the last ${n} days`;
  if (form === 'caption') return `Last ${n} days`;
  if (form === 'last') return `the last ${n} days`;
  return `${n} days`;
}

/** A count of days that is not a window: "1 day", "5 days". */
export function nDays(n) {
  return `${n} ${Number(n) === 1 ? 'day' : 'days'}`;
}

/** A day label's local midnight Date from 'YYYY-MM-DD' or a pg DATE sent as midnight on the API's clock (nearest UTC midnight); never an instant. */
export function calendarDay(v) {
  if (v == null || v === '') return null;
  const t = new Date(v).getTime();
  if (!Number.isFinite(t)) return null;
  const [y, m, d] = new Date(Math.round(t / DAY_MS) * DAY_MS).toISOString().slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * The first letter up, the rest as written: "email" -> "Email", "no reply yet"
 * -> "No reply yet". For a sentence or label that is already cased right apart
 * from its start. (sentenceCase below also tidies underscores and SHOUTING.)
 */
export function capFirst(value) {
  if (value == null || value === '') return value;
  const t = String(value);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * A status or category word as a label: "APPROVED" -> "Approved",
 * "pending_review" -> "Pending review", "email" -> "Email". Only a SHOUTED
 * value (all caps, longer than a short code like COD or UTM) is lowered
 * first, so a value written in mixed case keeps its own capitals.
 */
export function sentenceCase(value) {
  if (value == null || value === '') return value;
  let t = String(value).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  const letters = t.replace(/[^A-Za-z]/g, '');
  if (letters.length > 3 && letters === letters.toUpperCase()) t = t.toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function tidyCase(value) {
  if (value == null) return value;
  const t = String(value).trim();
  const letters = t.replace(/[^A-Za-z]/g, '');
  if (!letters) return t;
  const allUp = letters === letters.toUpperCase();
  const allLow = letters === letters.toLowerCase();
  if (!allUp && !allLow) return t;
  // A short all-caps code (state DL, KA, country IN) is meant to be caps.
  if (allUp && letters.length <= 3 && !/\s/.test(t)) return t;
  return t.toLowerCase().replace(/([^\s\-'’./(]+)/g, (w, _m, i) =>
    (i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)));
}

/** A person's display name from first + last, tidied; falls back as given. */
export function personName(first, last, fallback = '') {
  const n = [first, last].map((x) => (x == null ? '' : String(x).trim())).filter(Boolean).join(' ');
  return n ? tidyCase(n) : fallback;
}

/**
 * What to call a lead on a list or a drawer: the name, else the email, else
 * the phone, else, for a lead a client only ever sent hashed (their CRM posts
 * SHA-256, never the raw value: docs/CRM_LEAD_FLOW.md), "Hashed identity" and
 * the first characters of the hash, so two such leads can be told apart.
 */
export function leadLabel(lead, fallback = 'Unknown') {
  if (!lead) return fallback;
  const n = personName(lead.first_name, lead.last_name, '');
  if (n) return n;
  if (lead.email) return String(lead.email);
  if (lead.phone) return String(lead.phone);
  const h = lead.email_hash || lead.phone_hash;
  if (h) return `Hashed identity · ${String(h).slice(0, 8)}`;
  return fallback;
}

/** "Bengaluru, KA" from city + state, each tidied, empty parts skipped. */
export function placeName(...parts) {
  return parts.map((p) => tidyCase(p)).filter((p) => p && String(p).trim()).join(', ');
}

/**
 * A phone number grouped the way people read it: +91 92050 50225,
 * +1 415 555 0123, +358 40 097 6976. Anything it does not recognise is
 * returned unchanged rather than guessed at.
 */
export function formatPhone(value) {
  if (!value) return value;
  const raw = String(value).trim();
  const d = raw.replace(/[^\d+]/g, '');
  let m = d.match(/^\+?91(\d{10})$/);
  if (m) return `+91 ${m[1].slice(0, 5)} ${m[1].slice(5)}`;
  if (/^\d{10}$/.test(d) && /^[6-9]/.test(d)) return `${d.slice(0, 5)} ${d.slice(5)}`;
  m = d.match(/^\+1(\d{10})$/);
  if (m) return `+1 ${m[1].slice(0, 3)} ${m[1].slice(3, 6)} ${m[1].slice(6)}`;
  if (/^\+\d{8,15}$/.test(d)) {
    const digits = d.slice(1);
    // Country codes are 1, 2 or 3 digits and never ambiguous in this order.
    const cc = /^[17]/.test(digits) ? digits.slice(0, 1)
      : TWO_DIGIT_CC.has(digits.slice(0, 2)) ? digits.slice(0, 2) : digits.slice(0, 3);
    const rest = digits.slice(cc.length);
    if (rest.length < 6) return raw;
    if (rest.length === 8) return `+${cc} ${rest.slice(0, 4)} ${rest.slice(4)}`;
    const head = rest.length > 7 ? rest.slice(0, rest.length - 7) : '';
    const tail = rest.slice(head.length);
    return `+${cc} ${head ? `${head} ` : ''}${tail.slice(0, 3)} ${tail.slice(3)}`.replace(/\s+/g, ' ').trim();
  }
  return raw;
}
